import * as React from 'react';
import {
    fetchSavedSearchesIfNeeded,
    getSavedSearchesRecentFilters,
    onSavedSearchRecentFilterChange,
} from '@/redux/modules/savedSearch';
import { parseQueryParams } from '@/utils/queryParams';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import { useUnsubscribeFollowedSearch } from '../../hooks/useUnsubscribeFollowedSearch';
import isEqual from 'react-fast-compare';
import MySavedSearchPageManage from './MySavedSearchPageManage';

type QueryParams = {
    page: string;
    pageSize: string;
    unsubscribeAlert?: string;
};

type Props = {
    activeId: string;
    setActiveId: (id: string) => void;
};

const MySavedSearchPageManageContainer = ({ activeId, setActiveId }: Props) => {
    const dispatch = useAppDispatch();
    const { search } = useLocation();
    const queryParams = parseQueryParams<QueryParams>(search);
    const filters = useAppSelector(getSavedSearchesRecentFilters);

    const queryFilters = React.useMemo(
        () => ({
            page: Number(queryParams.page) || 1,
            pageSize: Number(queryParams.pageSize) || 24,
        }),
        [queryParams.page, queryParams.pageSize]
    );

    const updateStateFromQuery = React.useCallback(
        (force?: boolean) => {
            if (force || !isEqual(filters, queryFilters)) {
                dispatch(onSavedSearchRecentFilterChange(queryFilters));
            }
        },
        [dispatch, filters, queryFilters]
    );

    const fetchSavedSearches = React.useCallback(
        () => dispatch(fetchSavedSearchesIfNeeded(true, queryFilters.page, queryFilters.pageSize)),
        [dispatch, queryFilters.page, queryFilters.pageSize]
    );

    useUnsubscribeFollowedSearch(fetchSavedSearches);

    React.useEffect(() => {
        updateStateFromQuery(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        fetchSavedSearches();
    }, [fetchSavedSearches]);

    return (
        <MySavedSearchPageManage
            activeId={activeId}
            filters={{ ...filters, ...{ tab: 'searches' } }}
            queryParams={queryParams}
            setActiveId={setActiveId}
        />
    );
};

export default React.memo<Props>(MySavedSearchPageManageContainer);
